import { createServerFn } from "@tanstack/react-start";

import {
	OPERATION_COLUMNS,
	OPERATION_ITEMS_COLUMNS,
	OPERATION_STATUS,
	USER_ROLES,
} from "@onesolution/constants";
import { authMiddleware, mondayBoards } from "@onesolution/middlewares";
import { rolesCheck } from "@onesolution/middlewares/rolesCheck";
import { formatOperationUpdateColumnsForMutation } from "@onesolution/monday/format";
import { mondayClient } from "@onesolution/monday/monday";
import {
	type OperationMutationVariables,
	OperationValidator,
} from "@onesolution/utils/operationValidator";
import { createOperationSubItems, wrapWithUseMutation } from "..";

const handler = createServerFn({ method: "POST" })
	.middleware([
		authMiddleware,
		mondayBoards,
		rolesCheck([USER_ROLES.Admin, USER_ROLES.Member]),
	])
	.validator(OperationValidator)
	.handler(async ({ context, data: { data, files, thumbnail } }) => {
		if (!data.id) throw new Error("id must be set to perform an update");

		const columnValues = await formatOperationUpdateColumnsForMutation({
			...data,
			status:
				data.status === OPERATION_STATUS.Draft
					? data.status
					: OPERATION_STATUS.ToBeChecked,
		});

		const updatedOperation = await mondayClient.mutation.updateItem(
			{
				itemId: data.id,
				boardId: context.boards.briefs,
				columnValues,
			},
			context,
		);

		if (data.deletedProducts?.length) {
			await Promise.all(
				data.deletedProducts.map((deletedId) =>
					mondayClient.mutation.updateItem(
						{
							boardId: context.boards.briefSubitems,
							itemId: deletedId,
							columnValues: { [OPERATION_ITEMS_COLUMNS.status]: "DELETED" },
						},
						context,
					),
				),
			);
		}

		const subItemToCreate = data.subItems?.filter((item) => item.isNew);
		if (subItemToCreate?.length) {
			await createOperationSubItems(updatedOperation.id, subItemToCreate);
		}

		await Promise.all(
			files?.filter(Boolean).map((file) =>
				mondayClient.mutation.uploadFile({
					file: file!,
					itemId: data.id!,
					columnId: OPERATION_COLUMNS.files,
				}),
			) || [],
		);

		if (thumbnail || data.deleteThumbnail) {
			await mondayClient.mutation.deleteAllFiles({
				boardId: context.boards.briefs,
				itemId: data.id,
				columnId: OPERATION_COLUMNS.thumbnail,
			});
		}

		if (thumbnail) {
			await mondayClient.mutation.uploadFile({
				file: thumbnail!,
				itemId: data.id!,
				columnId: OPERATION_COLUMNS.thumbnail,
			});
		}

		if (!updatedOperation) {
			throw { error: "Error while updating operation." };
		}

		return updatedOperation;
	});

export const updateOperationOnServer = wrapWithUseMutation<
	typeof handler,
	OperationMutationVariables
>(handler, {
	mutationFn: async ({ files, thumbnail, ...params }) => {
		const formData = new FormData();

		for (const file of files || []) {
			formData.append("files", file, file.name);
		}

		if (thumbnail) formData.append("thumbnail", thumbnail, thumbnail.name);

		formData.append("data", JSON.stringify(params));

		const rep = await handler({ data: formData });

		return rep;
	},
});
